import { createContext, useContext, useState, useEffect, useRef, type ReactNode } from "react";
import { authService, type AuthState, type User } from '@/services/AuthService';
import { processReferralConversion } from "@/hooks/useReferralCapture";
import { clearSessionLocation } from "@/hooks/useSessionLocation";
import { Bugsnag } from "@/lib/bugsnag";
import { useToast } from "@/hooks/use-toast";

const bugsnagEnabled = !!(import.meta.env.VITE_BUGSNAG_API_KEY);

function setBugsnagUser(user: User | null) {
  if (!bugsnagEnabled) return;
  try {
    if (user) {
      Bugsnag.setUser(String(user.id), user.email, user.name || user.email);
    } else {
      Bugsnag.setUser(undefined, undefined, undefined);
    }
  } catch {
    // Never let Bugsnag crash auth flow
  }
}

interface EatzyUser {
  id: number | string;
  canonicalId?: string;
  email: string;
  firstName: string;
  lastName: string;
  name: string;
  phone?: string;
  avatar?: string;
  bio?: string;
  displayName?: string;
  kudozBalance?: number;
  lifetimeKudozEarned?: number;
  accountBalance?: string;
  trustScore?: number;
  trustTier?: 'newbie' | 'reliable' | 'trusted' | 'elite';
  totalReviews?: number;
  totalOrders?: number;
  profileCompletionPercent?: number;
}

export interface GuestLoginData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
}

interface AuthContextType {
  user: EatzyUser | null;
  token: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isGuestAccount: boolean;
  guestContactEmail: string | null;
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  signUp: (email: string, password: string, firstName: string, lastName: string) => Promise<{ success: boolean; error?: string }>;
  loginAsGuest: (data: GuestLoginData) => Promise<{ success: boolean; error?: string; errorCode?: string }>;
  loginWithProvider: (provider: 'google' | 'apple' | 'facebook', returnUrl?: string) => void;
  logout: () => void;
  refreshUser: () => Promise<void>;
  signInWithGoogle: () => void;
  signInWithFacebook: () => void;
  signInWithApple: () => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

// Helper function to convert User to EatzyUser
const convertToEatzyUser = (user: User | null): EatzyUser | null => {
  if (!user) return null;
  
  // Use firstName/lastName from user if available, otherwise parse from name
  const firstName = user.firstName || user.name?.split(' ')[0] || '';
  const lastName = user.lastName || user.name?.split(' ').slice(1).join(' ') || '';
  
  return {
    id: user.id,
    canonicalId: user.canonicalId,
    email: user.email,
    firstName,
    lastName,
    name: user.name || `${firstName} ${lastName}`.trim(),
    phone: user.phone,
    avatar: user.avatar,
    bio: user.bio,
    displayName: user.displayName,
    kudozBalance: user.kudozBalance,
    lifetimeKudozEarned: user.lifetimeKudozEarned,
    accountBalance: user.accountBalance,
    trustScore: user.trustScore,
    trustTier: user.trustTier,
    totalReviews: user.totalReviews,
    totalOrders: user.totalOrders,
    profileCompletionPercent: user.profileCompletionPercent
  };
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const [authState, setAuthState] = useState<AuthState>(() => ({
    user: null,
    token: null,
    isAuthenticated: false,
    isLoading: true
  }));

  const [hasInitialized, setHasInitialized] = useState(false);
  const { toast } = useToast();
  const isAuthenticatedRef = useRef(false);

  // Keep ref in sync with state for use in event listeners (avoids stale closure)
  useEffect(() => {
    isAuthenticatedRef.current = authState.isAuthenticated;
  }, [authState.isAuthenticated]);

  // Listen for expired-token events emitted by queryClient and auto-logout
  useEffect(() => {
    const handleExpired = () => {
      if (!isAuthenticatedRef.current) return;
      const currentPath = window.location.pathname + window.location.search;
      const redirectParam = currentPath !== '/login' ? `?returnUrl=${encodeURIComponent(currentPath)}` : '';
      authService.signOut().catch(() => {}).finally(() => {
        clearSessionLocation();
        sessionStorage.removeItem('partnerEatzyToken');
        sessionStorage.removeItem('adminEatzyToken');
        localStorage.removeItem('partnerRestaurantId');
        localStorage.removeItem('partnerRestaurantName');
        localStorage.removeItem('partnerRestaurantProfile');
        localStorage.removeItem('partnerUserId');
        localStorage.removeItem('guestContactEmail');
        setAuthState({
          user: null,
          token: null,
          isAuthenticated: false,
          isLoading: false,
        });
        setBugsnagUser(null);
        toast({
          title: "Session expired",
          description: "Please log in again to continue.",
          variant: "destructive",
        });
        window.location.href = `/login${redirectParam}`;
      });
    };
    window.addEventListener('auth:expired', handleExpired);
    return () => window.removeEventListener('auth:expired', handleExpired);
  }, [toast]);

  // Initialize auth state on mount
  useEffect(() => {
    if (hasInitialized) return;
    
    const initAuth = async () => {
      console.log('🔄 useAuth: Initializing auth state...');
      const storedState = authService.getAuthState();
      
      if (storedState.token) {
        console.log('🔍 useAuth: Verifying stored token...');
        const user = await authService.verifyToken();
        if (user) {
          console.log('✅ useAuth: Token valid, setting authenticated state');
          // Only persist enriched user if trust fields are present (ensures we don't overwrite with incomplete data)
          if (user.trustScore !== undefined) {
            authService.updateStoredUser(user);
          }
          setAuthState({
            user,
            token: storedState.token,
            isAuthenticated: true,
            isLoading: false
          });
          setBugsnagUser(user);
          setHasInitialized(true);
          return;
        } else {
          console.log('❌ useAuth: Token invalid, clearing stored auth');
          await authService.signOut();
        }
      }
      // No valid auth found
      console.log('🚫 useAuth: No valid auth found, setting unauthenticated state');
      setAuthState({
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false
      });
      setHasInitialized(true);
    };

    initAuth();
  }, [hasInitialized]);

  const login = async (email: string, password: string) => {
    try {
      setAuthState(prev => ({ ...prev, isLoading: true }));
      
      const { token, user } = await authService.signIn(email, password);
      
      setAuthState({
        user,
        token,
        isAuthenticated: true,
        isLoading: false
      });
      setBugsnagUser(user);
      
      await processReferralConversion();
      
      return { success: true };
    } catch (error: any) {
      setAuthState(prev => ({ ...prev, isLoading: false }));
      console.error('[useAuth] Login error:', error);
      return { success: false, error: error.message || 'Login failed' };
    }
  };

  const signUp = async (email: string, password: string, firstName: string, lastName: string) => {
    try {
      setAuthState(prev => ({ ...prev, isLoading: true }));
      
      const { token, user } = await authService.signUp(email, password, firstName, lastName);
      
      setAuthState({
        user,
        token,
        isAuthenticated: true,
        isLoading: false
      });
      setBugsnagUser(user);
      
      await processReferralConversion();
      
      return { success: true };
    } catch (error: any) {
      setAuthState(prev => ({ ...prev, isLoading: false }));
      console.error('[useAuth] Sign up error:', error);
      return { success: false, error: error.message || 'Sign up failed' };
    }
  };

  const loginAsGuest = async (data: GuestLoginData): Promise<{ success: boolean; error?: string; errorCode?: string }> => {
    try {
      const response = await fetch('/api/auth/guest/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      const body = await response.json();
      if (!response.ok) {
        return {
          success: false,
          error: body.message || body.error || 'Could not create guest session',
          errorCode: body.error || undefined,
        };
      }
      if (!body.token || typeof body.token !== 'string' || !body.user || !body.user.id) {
        console.error('[useAuth] loginAsGuest: malformed response payload', body);
        return { success: false, error: 'Unexpected response from server. Please try again.' };
      }
      if (body.eatzyAccessToken) {
        sessionStorage.setItem('adminEatzyToken', body.eatzyAccessToken);
        sessionStorage.setItem('partnerEatzyToken', body.eatzyAccessToken);
        localStorage.setItem('eatzyUserToken', body.eatzyAccessToken);
        if (body.eatzyRefreshToken) localStorage.setItem('eatzyRefreshToken', body.eatzyRefreshToken);
        if (body.eatzyTokenExpires) localStorage.setItem('eatzyTokenExpires', String(body.eatzyTokenExpires));
      }
      const guestUser: User = {
        id: body.user.id ?? '',
        email: body.user.email ?? '',
        name: body.user.name ?? `${body.user.firstName ?? ''} ${body.user.lastName ?? ''}`.trim(),
        avatar: body.user.avatar,
        bio: body.user.bio,
        displayName: body.user.displayName,
        firstName: body.user.firstName,
        lastName: body.user.lastName,
        phone: body.user.phone,
        kudozBalance: body.user.kudozBalance,
        lifetimeKudozEarned: body.user.lifetimeKudozEarned,
        accountBalance: body.user.accountBalance,
        trustScore: body.user.trustScore,
        trustTier: body.user.trustTier,
        totalReviews: body.user.totalReviews,
        totalOrders: body.user.totalOrders,
        profileCompletionPercent: body.user.profileCompletionPercent,
        followersCount: body.user.followersCount,
        followingCount: body.user.followingCount,
        isGuestAccount: true,
      };
      localStorage.setItem('auth_token', body.token);
      localStorage.setItem('auth_user', JSON.stringify(guestUser));
      if (body.guestContactEmail) {
        localStorage.setItem('guestContactEmail', body.guestContactEmail);
      }
      setAuthState({
        user: guestUser,
        token: body.token,
        isAuthenticated: true,
        isLoading: false,
      });
      setBugsnagUser(guestUser);
      await processReferralConversion();
      return { success: true };
    } catch (error: any) {
      console.error('[useAuth] loginAsGuest error:', error);
      return { success: false, error: error.message || 'Guest sign-in failed' };
    }
  };

  const loginWithProvider = (provider: 'google' | 'apple' | 'facebook', returnUrl: string = "/") => {
    // Use the consistent /api/auth/{provider} flow
    // returnUrl is stored in sessionStorage to redirect after auth callback
    if (returnUrl && returnUrl !== '/') {
      sessionStorage.setItem('auth_return_url', returnUrl);
    }
    window.location.href = `/api/auth/${provider}`;
  };

  const logout = async () => {
    try {
      await authService.signOut();
    } catch (error) {
      console.error('[useAuth] Logout error:', error);
    } finally {
      clearSessionLocation();
      sessionStorage.removeItem('partnerEatzyToken');
      sessionStorage.removeItem('adminEatzyToken');
      localStorage.removeItem('partnerRestaurantId');
      localStorage.removeItem('partnerRestaurantName');
      localStorage.removeItem('partnerRestaurantProfile');
      localStorage.removeItem('partnerUserId');
      localStorage.removeItem('guestContactEmail');
      setAuthState({
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false
      });
      setBugsnagUser(null);
    }
  };

  const refreshUser = async () => {
    try {
      const user = await authService.verifyToken();
      if (user) {
        // Only persist enriched user if trust fields are present
        if (user.trustScore !== undefined) {
          authService.updateStoredUser(user);
        }
        setAuthState(prev => ({
          ...prev,
          user,
          isAuthenticated: true
        }));
      }
    } catch (error) {
      console.error('[useAuth] Refresh user error:', error);
    }
  };

  const signInWithGoogle = () => {
    console.log('[useAuth] Initiating Google sign in...');
    authService.signInWithGoogle();
  };

  const signInWithFacebook = () => {
    console.log('[useAuth] Initiating Facebook sign in...');
    authService.signInWithFacebook();
  };

  const signInWithApple = () => {
    console.log('[useAuth] Initiating Apple sign in...');
    authService.signInWithApple();
  };

  const eatzyUser = convertToEatzyUser(authState.user);
  const isGuestAccount = authState.user?.isGuestAccount === true;
  const guestContactEmail = isGuestAccount
    ? (localStorage.getItem('guestContactEmail') ?? null)
    : null;

  return (
    <AuthContext.Provider
      value={{
        user: eatzyUser,
        token: authState.token,
        isLoading: authState.isLoading,
        isAuthenticated: authState.isAuthenticated,
        isGuestAccount,
        guestContactEmail,
        login,
        signUp,
        loginAsGuest,
        loginWithProvider,
        logout,
        refreshUser,
        signInWithGoogle,
        signInWithFacebook,
        signInWithApple
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
